import { useState } from 'react'
import { daysUntilReset, monthLabel } from '../engine'
import { formatMoney, parseMoney } from '../../lib/money'
import { navigate } from '../../lib/router'
import { useStore } from '../../store'
import {
  Bar,
  Card,
  Chip,
  Dialog,
  Empty,
  Money,
  StatTile,
  TopBar,
} from '../../ui/parts'
import {
  addAdjustment,
  completeEarnAction,
  deleteItem,
  deriveActions,
  deriveBudget,
  deriveItems,
  setOverridePrice,
  type ItemView,
} from '../repo'
import type { EarnAction } from '../types'
import { AddItemSheet } from './AddItemSheet'
import { CheckSheet } from './CheckSheet'
import { EarnActionCard } from './EarnActionCard'

type Filter = 'all' | 'wishlist' | 'purchased'

export function BudgetHome() {
  const { snapshot, mutate, showToast } = useStore()
  const [filter, setFilter] = useState<Filter>('all')
  const [adding, setAdding] = useState(false)
  const [checking, setChecking] = useState<ItemView | null>(null)
  const [editingPrice, setEditingPrice] = useState<ItemView | null>(null)
  const [deleting, setDeleting] = useState<ItemView | null>(null)
  const [claiming, setClaiming] = useState<EarnAction | null>(null)
  const [adjusting, setAdjusting] = useState(false)

  if (!snapshot) return null

  const budget = deriveBudget(snapshot)
  const items = deriveItems(snapshot)
  const actions = deriveActions(snapshot)
  const visible = items.filter((i) =>
    filter === 'all' ? true : filter === 'wishlist' ? i.status === 'wishlist' : i.status === 'purchased'
  )

  const spentFraction = budget.spentThisMonthFils / Math.max(1, budget.monthlyAllowanceFils)

  return (
    <div className="screen">
      <TopBar
        title="Budget management"
        subtitle={monthLabel(budget.currentMonth)}
        actions={
          <>
            <button type="button" className="icon-btn" onClick={() => navigate('/budget/log')}>
              ☰
            </button>
            <button type="button" className="icon-btn" onClick={() => navigate('/budget/settings')}>
              ⚙
            </button>
          </>
        }
      />

      <div className="stack">
        <Card>
          <div className="label">Available now</div>
          <div className="big-amount">
            <Money fils={budget.availableNowFils} currency />
          </div>

          <div style={{ marginTop: 14 }}>
            <Bar fraction={spentFraction} />
            <div className="row between" style={{ marginTop: 6 }}>
              <span className="label tnum">
                {formatMoney(budget.spentThisMonthFils)} spent this month
              </span>
              <span className="label tnum">of {formatMoney(budget.monthlyAllowanceFils)}</span>
            </div>
          </div>

          <div className="stats" style={{ marginTop: 14 }}>
            <StatTile
              label="Monthly budget"
              value={formatMoney(budget.monthlyAllowanceFils)}
              glyph="▤" tone="var(--mist)"
            />
            <StatTile
              label="Rolled over"
              value={formatMoney(budget.rolledOverFils)}
              glyph="↻" tone="var(--mist)"
            />
            <StatTile
              label="Committed ahead"
              value={formatMoney(budget.committedFutureFils)}
              glyph="◷" tone="var(--mist)"
            />
            <StatTile
              label="Resets in"
              value={`${daysUntilReset()} days`}
              glyph="◌" tone="var(--mist)"
            />
          </div>

          <button
            type="button"
            className="btn ghost small"
            style={{ marginTop: 10, paddingLeft: 0 }}
            onClick={() => setAdjusting(true)}
          >
            Adjust budget
          </button>
        </Card>

        {snapshot.settings.earnEnabled && actions.length > 0 ? (
          <>
            <div className="row between section-title">
              <span>Earn extra budget</span>
              <span className="label">{actions.length} available</span>
            </div>
            {actions.map((action) => (
              <EarnActionCard
                key={action.id}
                action={action}
                onComplete={() => setClaiming(action)}
              />
            ))}
          </>
        ) : null}

        <div className="filters" style={{ marginTop: 4 }}>
          {(['all', 'wishlist', 'purchased'] as Filter[]).map((option) => (
            <button
              key={option}
              type="button"
              className={`filter ${filter === option ? 'on' : ''}`}
              onClick={() => setFilter(option)}
            >
              {option === 'all' ? 'All' : option === 'wishlist' ? 'Wishlist' : 'Purchased'}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <Empty
            title="Nothing on the list yet"
            body="Add something you are saving up for. Paste a webshop link and the details get filled in for you."
            action={
              <button type="button" className="btn" onClick={() => setAdding(true)}>
                Add your first item
              </button>
            }
          />
        ) : null}

        {visible.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            onCheck={() => setChecking(item)}
            onEditPrice={() => setEditingPrice(item)}
            onDelete={() => setDeleting(item)}
          />
        ))}
      </div>

      <button type="button" className="fab" onClick={() => setAdding(true)} aria-label="Add item">
        +
      </button>

      {adding ? <AddItemSheet onClose={() => setAdding(false)} /> : null}

      {checking ? <CheckSheet item={checking} onClose={() => setChecking(null)} /> : null}

      {editingPrice ? (
        <PriceDialog
          item={editingPrice}
          onClose={() => setEditingPrice(null)}
          onSave={async (fils) => {
            const id = editingPrice.id
            setEditingPrice(null)
            await mutate((s) => setOverridePrice(s, id, fils))
          }}
        />
      ) : null}

      {deleting ? (
        <Dialog
          title={`Remove "${deleting.title}"?`}
          body={
            deleting.purchase
              ? `You get the full ${formatMoney(deleting.purchase.totalFils)} AED back. Anything already charged is credited to this month, and any remaining installments are cancelled.`
              : 'This only removes it from your list. Nothing has been spent on it.'
          }
          confirmLabel="Remove"
          danger
          cancelLabel="Keep"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            const { id, title } = deleting
            setDeleting(null)
            if (await mutate((s) => deleteItem(s, id))) showToast(`Removed "${title}"`)
          }}
        />
      ) : null}

      {claiming ? (
        <Dialog
          title="Claim this reward?"
          body={
            <>
              <strong>{claiming.title}</strong> pays{' '}
              <Money fils={claiming.rewardFils} currency /> into this month's budget.
              {claiming.repeatable ? ' It stays on the list and can be claimed again.' : ''}
            </>
          }
          confirmLabel="Yes, complete it"
          cancelLabel="Not yet"
          onCancel={() => setClaiming(null)}
          onConfirm={async () => {
            const { id, title } = claiming
            setClaiming(null)
            // Work out the reward from the current snapshot first, so the toast can name
            // the amount even though the change itself is applied inside mutate.
            const preview = completeEarnAction(snapshot, id)
            if (preview.reward === null) {
              showToast('That action is no longer available')
              return
            }
            if (await mutate((s) => completeEarnAction(s, id).snapshot)) {
              showToast(`Earned ${formatMoney(preview.reward)} AED — "${title}"`)
            }
          }}
        />
      ) : null}

      {adjusting ? (
        <AdjustDialog
          onClose={() => setAdjusting(false)}
          onApply={async (fils, note) => {
            setAdjusting(false)
            if (await mutate((s) => addAdjustment(s, fils, note))) {
              showToast(
                fils > 0
                  ? `Added ${formatMoney(fils)} AED to this month`
                  : `Removed ${formatMoney(-fils)} AED from this month`
              )
            }
          }}
        />
      ) : null}
    </div>
  )
}

/** One item per line: text on the left where the eye starts, thumbnail on the right. */
function ItemRow({
  item,
  onCheck,
  onEditPrice,
  onDelete,
}: {
  item: ItemView
  onCheck: () => void
  onEditPrice: () => void
  onDelete: () => void
}) {
  const purchased = item.status === 'purchased'

  return (
    <Card tight>
      <div className="item-row">
        <div className="grow">
          <div className="row">
            <span className="item-brand grow">{item.brand ?? 'No brand'}</span>
            {purchased ? (
              <Chip tone="mint">
                {item.purchase && item.purchase.totalCount > 1
                  ? `${item.purchase.paidCount}/${item.purchase.totalCount} paid`
                  : 'Purchased'}
              </Chip>
            ) : null}
          </div>
          <div className="item-title">{item.title}</div>
          <div className="row" style={{ alignItems: 'baseline' }}>
            <span className="item-price">
              <Money fils={item.effectivePriceFils} currency />
            </span>
            {item.isOverridden ? (
              <span className="muted strike tnum" style={{ fontSize: 13 }}>
                {formatMoney(item.priceFils)}
              </span>
            ) : null}
          </div>
          {item.currencyRaw && item.currencyRaw !== 'AED' ? (
            <div className="negative" style={{ fontSize: 12 }}>
              Scraped in {item.currencyRaw} — check the amount
            </div>
          ) : null}
        </div>

        {item.imageUrl ? (
          <img className="item-thumb" src={item.imageUrl} alt="" loading="lazy" />
        ) : (
          <div className="item-thumb" aria-hidden>
            ▧
          </div>
        )}
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        {!purchased ? (
          <>
            <button type="button" className="btn small" onClick={onCheck}>
              Check
            </button>
            <button type="button" className="btn ghost small" onClick={onEditPrice}>
              Edit price
            </button>
          </>
        ) : null}
        <span className="grow" />
        <button type="button" className="icon-btn" onClick={onDelete} aria-label="Remove">
          ✕
        </button>
      </div>
    </Card>
  )
}

/**
 * Discounts apply against the original scraped price, not the current override, so
 * entering 20 twice does not compound into 36%.
 */
function PriceDialog({
  item,
  onClose,
  onSave,
}: {
  item: ItemView
  onClose: () => void
  onSave: (fils: number | null) => void
}) {
  const [text, setText] = useState(formatMoney(item.effectivePriceFils))
  const [percentText, setPercentText] = useState('')

  const parsed = parseMoney(text)
  const percent = percentText === '' ? null : Number(percentText)
  const percentValid = percent !== null && Number.isInteger(percent) && percent >= 1 && percent <= 100
  const discounted = percentValid ? item.priceFils - Math.round((item.priceFils * percent!) / 100) : null

  return (
    <div className="scrim" style={{ alignItems: 'center' }} onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Edit price</h3>

        <label className="field">
          <span>Price in AED</span>
          <input
            type="text"
            inputMode="decimal"
            value={text}
            className={parsed === null || parsed <= 0 ? 'error' : ''}
            onChange={(e) => setText(e.target.value)}
          />
        </label>

        <div className="label" style={{ marginTop: 10 }}>
          Apply a discount to {formatMoney(item.priceFils)}
        </div>
        <div className="row" style={{ marginTop: 6 }}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="e.g. 15"
            value={percentText}
            className={percentText !== '' && !percentValid ? 'error' : ''}
            onChange={(e) => setPercentText(e.target.value.replace(/\D/g, '').slice(0, 3))}
            style={{ flex: 1 }}
          />
          <span className="muted">%</span>
          <button
            type="button"
            className="btn small"
            disabled={!percentValid}
            onClick={() => discounted !== null && setText(formatMoney(discounted))}
          >
            Apply
          </button>
        </div>
        {discounted !== null ? (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {percent}% off → {formatMoney(discounted)} AED
          </div>
        ) : null}

        <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          The original price is kept, so you can always reset.
        </div>

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          {item.isOverridden ? (
            <button type="button" className="btn ghost" onClick={() => onSave(null)}>
              Reset
            </button>
          ) : null}
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={parsed === null || parsed <= 0}
            onClick={() => parsed !== null && onSave(parsed)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Add / Remove is a toggle rather than a typed minus sign: the numeric keyboard on
 * Android has no minus key, so asking for "-100" was impossible to enter.
 */
function AdjustDialog({
  onClose,
  onApply,
}: {
  onClose: () => void
  onApply: (fils: number, note: string) => void
}) {
  const [direction, setDirection] = useState<'add' | 'remove'>('add')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')

  const parsed = parseMoney(amount)
  const valid = parsed !== null && parsed > 0

  return (
    <div className="scrim" style={{ alignItems: 'center' }} onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Adjust this month</h3>
        <div className="muted" style={{ fontSize: 13 }}>
          A one-off change to this month only. It shows up in the log.
        </div>

        <div className="segmented" style={{ margin: '14px 0 12px' }}>
          <button
            type="button"
            className={direction === 'add' ? 'on' : ''}
            onClick={() => setDirection('add')}
          >
            Add budget
          </button>
          <button
            type="button"
            className={direction === 'remove' ? 'on' : ''}
            onClick={() => setDirection('remove')}
          >
            Remove budget
          </button>
        </div>

        <label className="field">
          <span>Amount in AED</span>
          <input
            type="text"
            inputMode="decimal"
            placeholder="250"
            value={amount}
            className={amount !== '' && !valid ? 'error' : ''}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Reason</span>
          <input
            type="text"
            placeholder="bonus, refund, …"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={!valid}
            onClick={() => valid && onApply(direction === 'add' ? parsed! : -parsed!, note)}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}
